import { useEffect, useState } from 'react';
import { useMagnetic, useScrollTo, reduced } from '../lib/motion';
import './Hero.css';

const ROLES = [
  'Full-Stack Engineer',
  'Product Builder',
  'Cloud & DevOps',
  'Founder — AlgorX Software',
];

/* The build index — first thing a visitor should see */
const PRODUCTS = [
  {
    name: 'Rovaine',
    kind: 'Consumer platform',
    url: 'https://github.com/rarespoanta27/Rovaine',
    stack: 'React · Node · Stripe',
  },
  {
    name: 'DeadliQ',
    kind: 'B2B SaaS',
    url: 'https://github.com/rarespoanta27/DeadliQ',
    stack: 'MERN · Cloud Vision',
  },
  {
    name: 'TuniQ',
    kind: 'Developer tooling',
    url: 'https://github.com/rarespoanta27/TuniQ',
    stack: 'React · Python',
  },
];

/* Rotating role with a monospace caret */
function useRotator(list, ready) {
  const [text, setText] = useState('');
  const [i, setI] = useState(0);
  const [del, setDel] = useState(false);

  useEffect(() => {
    if (!ready) return undefined;
    if (reduced()) { setText(list[0]); return undefined; }
    const cur = list[i];
    const speed = del ? 26 : 62;
    const t = setTimeout(() => {
      if (!del) {
        if (text.length < cur.length) setText(cur.slice(0, text.length + 1));
        else setTimeout(() => setDel(true), 2100);
      } else if (text.length > 0) {
        setText(cur.slice(0, text.length - 1));
      } else {
        setDel(false);
        setI((p) => (p + 1) % list.length);
      }
    }, speed);
    return () => clearTimeout(t);
  }, [text, del, i, list, ready]);

  return text;
}

/* Live clock in Timișoara */
function useLocalTime() {
  const [t, setT] = useState('');
  useEffect(() => {
    const fmt = () =>
      new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false, timeZone: 'Europe/Bucharest',
      }).format(new Date());
    setT(fmt());
    const id = setInterval(() => setT(fmt()), 1000);
    return () => clearInterval(id);
  }, []);
  return t;
}

export default function Hero({ ready }) {
  const role = useRotator(ROLES, ready);
  const time = useLocalTime();
  const scrollTo = useScrollTo();
  const magA = useMagnetic(0.22, 10);
  const magB = useMagnetic(0.22, 10);

  const on = ready ? 'in' : '';

  return (
    <section id="top" className="hero" aria-label="Introduction">
      <div className="hero-grid shell" itemScope itemType="https://schema.org/Person">

        {/* ── Left ─────────────────────────────────────────── */}
        <div className="hero-main">
          <div className={`hero-status ${on}`}>
            <span className="avail">
              <i className="avail-led" />
              Available for new projects
            </span>
            <span className="hero-status-sep" />
            <span className="mono hero-status-loc">TIMIȘOARA · {time}</span>
          </div>

          <h1 className="hero-name display-xl" itemProp="name">
            <span className={`mask-line ${on}`} style={{ '--ml-d': '80ms' }}><span>POANTA</span></span>
            <span className={`mask-line ${on}`} style={{ '--ml-d': '200ms' }}>
              <span>RAREȘ<i className="hero-dot" /></span>
            </span>
          </h1>

          <meta itemProp="alternateName" content="Rares Poanta" />
          <meta itemProp="jobTitle" content="Full-Stack Software Engineer & Founder, AlgorX Software SRL" />
          <meta itemProp="email" content="rarespoanta10@gmail.com" />

          <div className={`hero-role ${on}`}>
            <span className="hero-role-bracket">[</span>
            <span className="hero-role-text">{role}</span>
            <span className="hero-caret" />
            <span className="hero-role-bracket">]</span>
          </div>

          <p className={`hero-lede ${on}`} itemProp="description">
            I build <strong>complete software products</strong> — web platforms, SaaS
            and the cloud infrastructure under them. Three of them are open source, so
            you can read every decision instead of taking my word for it.
          </p>

          <div className={`hero-cta ${on}`}>
            <span ref={magA} className="mag">
              <button className="btn btn--volt btn--lg" onClick={() => scrollTo('work')}>
                See what I&rsquo;ve shipped
                <svg className="btn-ico btn-ico--down" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="4" x2="12" y2="19" /><polyline points="6 13 12 19 18 13" />
                </svg>
              </button>
            </span>
            <span ref={magB} className="mag">
              <a className="btn btn--lg" href="mailto:rarespoanta10@gmail.com">
                Start a project
                <svg className="btn-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="7" y1="17" x2="17" y2="7" /><polyline points="7 7 17 7 17 17" />
                </svg>
              </a>
            </span>
          </div>

          <div className={`hero-social ${on}`}>
            <span className="mono hero-social-label">Elsewhere</span>
            <a href="https://github.com/rarespoanta27" target="_blank" rel="noopener noreferrer me" itemProp="sameAs">GitHub</a>
            <a href="https://www.linkedin.com/in/rares-poanta-b3970b309" target="_blank" rel="noopener noreferrer me" itemProp="sameAs">LinkedIn</a>
            <a href="https://www.instagram.com/rares.poanta/" target="_blank" rel="noopener noreferrer me" itemProp="sameAs">Instagram</a>
          </div>
        </div>

        {/* ── Right — production console ───────────────────── */}
        <aside className={`hero-panel ${on}`} aria-label="Selected builds">
          <div className="hero-panel-bar">
            <span className="hero-panel-dots"><i /><i /><i /></span>
            <span className="mono">build.index</span>
            <span className="hero-panel-live">open source</span>
          </div>

          <div className="hero-products">
            {PRODUCTS.map((p, i) => (
              <a
                className="hero-product"
                key={p.name}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                data-cur="SOURCE"
                style={{ transitionDelay: `${560 + i * 100}ms` }}
              >
                <span className="hero-product-led" />
                <span className="hero-product-body">
                  <span className="hero-product-name">{p.name}</span>
                  <span className="hero-product-kind">{p.kind}</span>
                </span>
                <span className="hero-product-stack">{p.stack}</span>
                <svg className="hero-product-go" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="7" y1="17" x2="17" y2="7" /><polyline points="7 7 17 7 17 17" />
                </svg>
              </a>
            ))}
          </div>

          <div className="hero-panel-foot">
            {[
              { n: '100+', l: 'Projects shipped' },
              { n: '3', l: 'Open source' },
              { n: '24h', l: 'Reply window' },
            ].map((s) => (
              <div className="hero-stat" key={s.l}>
                <span className="hero-stat-n">{s.n}</span>
                <span className="hero-stat-l">{s.l}</span>
              </div>
            ))}
          </div>
        </aside>
      </div>

      {/* Scroll cue */}
      <div className={`hero-cue ${on}`} aria-hidden="true">
        <span className="mono">Scroll</span>
        <span className="hero-cue-rail"><i /></span>
      </div>
    </section>
  );
}
